import React, { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Grid, capitalize, MenuItem, Select, Stack, Skeleton, Box } from '@mui/material'

import { useStores } from '@trivie/core'
import { DateFilter } from '@trivie/ui-web/components/date-filter'
import { Header } from '@trivie/ui-web/components/header'
import { Insight } from '@trivie/ui-web/components/insight'
import { Mastery } from '@trivie/ui-web/components/mastery'
import { Text } from '@trivie/ui-web/components/text'
import { GenerateReport } from '@trivie/ui-web/components/generate-report'
import { StrongestWeakest } from '@trivie/ui-web/components/strongest-weakest'
import { Panel } from '@trivie/ui-web/components/panel/panel'
import { LineGraph } from '@trivie/ui-web/components/line-graph'
import { color } from '@trivie/ui-web/theme'
import { ArrowDropDownRounded } from '@mui/icons-material'
import { DateTime } from 'luxon'
import { ReportsApi } from '@trivie/core/src/services/api-objects'

export const KnowledgeScreen: React.FunctionComponent = observer(() => {
  const { reportStore } = useStores()
  const {
    getKnowledgeMastery,
    getTopicGaps,
    getKnowledgeBaseStats,
    getQuestionGaps,
    knowledgeBaseStats,
    knowledgeMastery,
    topicGaps,
    questionGaps,
    knowledgeGainHistory,
    getKnowledgeGainHistory,
  } = reportStore

  const [timespan, setTimespan] = useState('All Time')
  const [userStatus, setUserStatus] = useState('all')
  const [start, setStart] = useState<any>(DateTime.local(1971).startOf('day').toISO())
  const [end, setEnd] = useState<any>(DateTime.local().endOf('day').toISO())

  const [loadingMastery, setLoadingMastery] = useState(true)
  const [loadingTopicGaps, setLoadingTopicGaps] = useState(true)
  const [loadingQuestionGaps, setLoadingQuestionGaps] = useState(true)
  const [loadingKnowledgeBase, setLoadingKnowledgeBase] = useState(true)
  const [loadingKnowledgeHistory, setLoadingKnowledgeHistory] = useState(true)

  const masteryCancelRef = useRef<any>()
  const topicGapRef = useRef<any>()
  const questionGapRef = useRef<any>()
  const knowledgeBaseRef = useRef<any>()
  const knowledgeHistoryRef = useRef<any>()

  useEffect(() => {
    return () => {
      cancelRequests()
    }
  }, [])

  const cancelRequests = () => {
    if (topicGapRef.current) {
      topicGapRef.current.cancel()
    }
    if (questionGapRef.current) {
      questionGapRef.current.cancel()
    }
    if (knowledgeBaseRef.current) {
      knowledgeBaseRef.current.cancel()
    }
    if (knowledgeHistoryRef.current) {
      knowledgeHistoryRef.current.cancel()
    }
    if (masteryCancelRef.current) {
      masteryCancelRef.current.cancel()
    }
  }

  const load = (startDate: string, endDate: string, status: string) => {
    setLoadingMastery(true)
    setLoadingTopicGaps(true)
    setLoadingQuestionGaps(true)
    setLoadingKnowledgeBase(true)
    setLoadingKnowledgeHistory(true)

    cancelRequests()

    const masteryCancel = ReportsApi.getCancelToken()
    masteryCancelRef.current = masteryCancel
    getKnowledgeMastery(startDate, endDate, status, masteryCancel.token)
      .then(() => setLoadingMastery(false))
      .catch((e) => setLoadingMastery(true))

    const topicGapCancel = ReportsApi.getCancelToken()
    topicGapRef.current = topicGapCancel
    getTopicGaps(startDate, endDate, status, topicGapCancel.token)
      .then(() => setLoadingTopicGaps(false))
      .catch((e) => setLoadingTopicGaps(true))

    const questionGapCancel = ReportsApi.getCancelToken()
    questionGapRef.current = questionGapCancel
    getQuestionGaps(startDate, endDate, status, questionGapCancel.token)
      .then(() => setLoadingQuestionGaps(false))
      .catch((e) => setLoadingQuestionGaps(true))

    const knowledgeBaseCancel = ReportsApi.getCancelToken()
    knowledgeBaseRef.current = knowledgeBaseCancel
    getKnowledgeBaseStats(startDate, endDate, status, knowledgeBaseCancel.token)
      .then(() => setLoadingKnowledgeBase(false))
      .catch((e) => setLoadingKnowledgeBase(true))

    const knowledgeHistoryCancel = ReportsApi.getCancelToken()
    knowledgeHistoryRef.current = knowledgeHistoryCancel
    getKnowledgeGainHistory(startDate, endDate, status, knowledgeHistoryCancel.token)
      .then(() => setLoadingKnowledgeHistory(false))
      .catch((e) => setLoadingKnowledgeHistory(true))
  }

  const onDateChange = async (startDateTime, endDateTime, label) => {
    setStart(startDateTime)
    setEnd(endDateTime)
    setTimespan(label as any)
    load(startDateTime, endDateTime, userStatus)
  }

  const onUserStatusChange = (e) => {
    setUserStatus(e.target.value)
    load(start, end, e.target.value)
  }

  return (
    <>
      <Header divider title="Knowledge" />
      <Stack mb={2} direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" gap={1}>
          <DateFilter onDateChange={onDateChange} />
          <Select
            sx={{ '& .MuiSelect-select:focus': { backgroundColor: 'transparent' } }}
            autoWidth
            renderValue={(value) => (
              <Text
                variant="labelMedium"
                text={`${capitalize(value)} Users`}
                style={{ color: color.shade70 }}
              />
            )}
            disableUnderline
            variant="standard"
            value={userStatus}
            IconComponent={ArrowDropDownRounded}
            onChange={onUserStatusChange}
          >
            <MenuItem value={'all'}>All Users</MenuItem>
            <MenuItem value={'active'}>Active Users</MenuItem>
            <MenuItem value={'deactivated'}>Deactivated Users</MenuItem>
          </Select>
        </Stack>
        <GenerateReport filename="knowledge" />
      </Stack>
      <Grid container spacing={2} pb="40px">
        <Grid size={{ sm: 12, lg: 8 }}>
          {loadingKnowledgeBase || loadingKnowledgeHistory ? (
            <Box minHeight={194} flex={1} sx={{ width: '100%', height: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          ) : (
            <Panel title="How much are people learning?">
              <Stack gap={2} direction="row" flexWrap="wrap">
                <Insight
                  noAveraging
                  unit="%"
                  timespan={timespan}
                  data={knowledgeGainHistory?.stats_history[1]?.data_points}
                  title="Baseline Knowledge"
                  value={`${knowledgeBaseStats?.starting_knowledge?.toFixed(1) || '0.0'}%`}
                />
                <Insight
                  noAveraging
                  unit="%"
                  timespan={timespan}
                  data={knowledgeBaseStats?.stats_history?.stats_history[0]?.data_points}
                  title="Current Knowledge"
                  value={`${knowledgeBaseStats?.current_knowledge?.toFixed(1) || '0.0'}%`}
                />
                <Insight
                  noAveraging
                  unit="%"
                  timespan={timespan}
                  data={knowledgeBaseStats?.stats_history?.stats_history[2]?.data_points}
                  title="Knowledge Gain"
                  value={`${knowledgeBaseStats?.knowledge_gain?.toFixed(1) || '0.0'}%`}
                />
              </Stack>
              <Text
                variant="body2Medium"
                color="text.secondary"
                text="We measure each person's baseline knowledge by their first answer to each question. As they answer more questions, their current knowledge rises, resulting in knowledge gain."
              />
            </Panel>
          )}
        </Grid>
        <Grid size={{ sm: 12, lg: 4 }}>
          {loadingMastery ? (
            <Box minHeight={194} flex={1} sx={{ width: '100%', height: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          ) : (
            <Panel fill title="Overall Content Mastery">
              <Stack height="100%" useFlexGap gap={4} justifyContent="center">
                <Text
                  style={{ fontSize: 48 }}
                  variant="headline"
                  text={
                    knowledgeMastery
                      ? `${Math.round(knowledgeMastery?.overall_mastery * 100) / 4}%`
                      : '0%'
                  }
                  component="div"
                />
                <LineGraph
                  height={44}
                  width="auto"
                  format={
                    timespan === 'Today' || timespan === 'Yesterday'
                      ? 'yyyy-MM-dd:hh'
                      : 'yyyy-MM-dd'
                  }
                  data={knowledgeMastery?.stats_history?.[0].data_points}
                />
              </Stack>
            </Panel>
          )}
        </Grid>
        {(!loadingTopicGaps && topicGaps?.weakest?.length) ||
        (!loadingQuestionGaps && questionGaps?.weakest?.length) ? (
          <Grid size={{ sm: 12, md: 6 }}>
            <StrongestWeakest
              style={{ height: '100%' }}
              type="Weakest"
              variant="medium"
              topics={topicGaps?.weakest}
              questions={questionGaps?.weakest}
            />
          </Grid>
        ) : (!loadingTopicGaps && !topicGaps?.weakest?.length) ||
          (!loadingQuestionGaps && !questionGaps?.weakest?.length) ? null : (
          <Grid size={{ sm: 12, md: 6 }}>
            <Box minHeight={280} flex={1} sx={{ width: '100%', height: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          </Grid>
        )}
        {(!loadingTopicGaps && topicGaps?.strongest?.length) ||
        (!loadingQuestionGaps && questionGaps?.strongest?.length) ? (
          <Grid size={{ sm: 12, md: 6 }}>
            <StrongestWeakest
              style={{ height: '100%' }}
              type="Strongest"
              variant="medium"
              topics={topicGaps?.strongest}
              questions={questionGaps?.strongest}
            />
          </Grid>
        ) : (!loadingTopicGaps && !topicGaps?.strongest?.length) ||
          (!loadingQuestionGaps && !questionGaps?.strongest?.length) ? null : (
          <Grid size={{ sm: 12, md: 6 }}>
            <Box minHeight={280} flex={1} sx={{ width: '100%', height: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          </Grid>
        )}

        <Grid size={12}>
          {loadingMastery ? (
            <Box minHeight={157} flex={1} sx={{ width: '100%', height: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          ) : (
            knowledgeMastery && (
              <Panel title="Mastery Distribution">
                <Stack gap={3} direction="row" flexWrap="wrap">
                  <Mastery
                    variant="green"
                    percentage={knowledgeMastery?.most}
                    message={`${knowledgeMastery?.most_user_count} ${
                      knowledgeMastery?.most_user_count > 1 ||
                      knowledgeMastery?.most_user_count === 0
                        ? 'people have'
                        : 'person has'
                    } mastered most of their questions.`}
                  />
                  <Mastery
                    variant="yellow"
                    percentage={knowledgeMastery?.some}
                    message={`${knowledgeMastery?.some_user_count} ${
                      knowledgeMastery?.some_user_count > 1 ||
                      knowledgeMastery?.some_user_count === 0
                        ? 'people have'
                        : 'person has'
                    } mastered some of their questions.`}
                  />
                  <Mastery
                    variant="red"
                    percentage={knowledgeMastery?.few}
                    message={`${knowledgeMastery?.few_user_count} ${
                      knowledgeMastery?.few_user_count > 1 || knowledgeMastery?.few_user_count === 0
                        ? 'people have'
                        : 'person has'
                    } mastered few of their questions.`}
                  />
                </Stack>
              </Panel>
            )
          )}
        </Grid>
      </Grid>
    </>
  )
})
